import { useState } from "react";
import { Plus, Building2, Trash2, ChevronDown, ChevronRight, RefreshCcw } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import {
  EXTENSION_TYPES, emptyExtension, buildExtensionShellItems, indicativeShellRate,
  buildFitoutRoomExtras,
} from "@/lib/extensionTemplates";
import { roomTotals, extensionTotal, extensionsAddedSqm } from "@/lib/calculations";
import { useFormatCurrency } from "@/lib/CurrencyContext";
import RoomCard from "@/components/RoomCard";
import { ROOM_TYPES, buildDefaultWorkItems } from "@/lib/roomTemplates";

function newId(prefix = "ext") {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return prefix + "-" + Math.random().toString(36).slice(2);
}

export default function ExtensionsTab({ project, onChange, costLibrary = null }) {
  const fmt = useFormatCurrency();
  const currency = project?.currency || "USD";
  const extensions = project?.extensions || [];
  const [createOpen, setCreateOpen] = useState(false);
  const [draft, setDraft] = useState({
    name: "",
    type: "single_storey",
    footprint_sqm: 20,
    storeys: 1,
    rooms: [], // [{tempId, room_type, name, size_sqm, finish_level}]
  });

  const grandTotal = extensions.reduce((s, e) => s + extensionTotal(e), 0);
  const addedSqm = extensionsAddedSqm(project);

  const draftFootprint = draft.type === "knockthrough" ? 0 : Number(draft.footprint_sqm) || 0;
  const draftUsedSqm = (draft.rooms || []).reduce((s, r) => s + (Number(r.size_sqm) || 0), 0);
  const draftRemaining = draftFootprint - draftUsedSqm;
  const draftOverbudget = draftFootprint > 0 && draftUsedSqm > draftFootprint;

  const replaceExtensions = (next) => onChange({ extensions: next });

  const addDraftRoom = () => {
    const tempId = "tmp-" + Math.random().toString(36).slice(2);
    // Suggested size: half remaining footprint, capped at a sensible minimum.
    const remaining = Math.max(0, draftFootprint - draftUsedSqm);
    const suggested = remaining > 0 ? Math.max(4, Math.floor(remaining / 2)) : 8;
    setDraft({
      ...draft,
      rooms: [
        ...(draft.rooms || []),
        { tempId, room_type: "bedroom", name: "", size_sqm: suggested, finish_level: "mid" },
      ],
    });
  };

  const updateDraftRoom = (tempId, patch) => {
    setDraft({
      ...draft,
      rooms: (draft.rooms || []).map((r) => (r.tempId === tempId ? { ...r, ...patch } : r)),
    });
  };

  const removeDraftRoom = (tempId) => {
    setDraft({
      ...draft,
      rooms: (draft.rooms || []).filter((r) => r.tempId !== tempId),
    });
  };

  const createExtension = () => {
    if (draftOverbudget) return;
    const meta = EXTENSION_TYPES.find((t) => t.value === draft.type) || EXTENSION_TYPES[0];
    const shell = buildExtensionShellItems({
      type: draft.type,
      footprint_sqm: draftFootprint,
      overrides: costLibrary?.extension_rates?.[draft.type] || {},
      currency,
      project,
    });
    const fitoutRooms = (draft.rooms || []).map((r) => {
      const size = Math.max(0, Number(r.size_sqm) || 0);
      const items = buildDefaultWorkItems({
        room_type: r.room_type,
        size_sqm: size,
        renovation_scope: "moderate",
        finish_level: r.finish_level,
        condition: r.condition || "tired",
        overrides: costLibrary?.room_rates?.[r.room_type] || {},
        currency,
      });
      // New-build fitout rooms need partitions, doors, ceilings, electrics
      // and (for habitable rooms) glazing — none of which live in the
      // renovation template because that template assumes those already exist.
      const extras = buildFitoutRoomExtras({
        room_type: r.room_type,
        size_sqm: size,
        overrides: costLibrary?.room_rates?.[r.room_type] || {},
        currency,
      });
      const rmeta = ROOM_TYPES.find((t) => t.value === r.room_type) || ROOM_TYPES[0];
      return {
        id: newId("room"),
        name: (r.name || "").trim() || rmeta.label,
        room_type: r.room_type,
        size_sqm: size,
        condition: "tired",
        renovation_scope: "moderate",
        finish_level: r.finish_level,
        work_items: [...extras, ...items],
      };
    });
    const ext = {
      id: newId(),
      name: draft.name?.trim() || meta.label,
      type: draft.type,
      footprint_sqm: draft.type === "knockthrough" ? 0 : draftFootprint,
      storeys: draft.type === "double_storey" ? 2 : 1,
      shell_items: shell,
      fitout_rooms: fitoutRooms,
      notes: "",
    };
    replaceExtensions([...extensions, ext]);
    setCreateOpen(false);
    setDraft({ name: "", type: "single_storey", footprint_sqm: 20, storeys: 1, rooms: [] });
  };

  const updateExtension = (id, patch) => {
    replaceExtensions(extensions.map((e) => (e.id === id ? { ...e, ...patch } : e)));
  };

  const deleteExtension = (id) => {
    replaceExtensions(extensions.filter((e) => e.id !== id));
  };

  const regenShell = (id) => {
    const target = extensions.find((e) => e.id === id);
    if (!target) return;
    const shell = buildExtensionShellItems({
      type: target.type,
      footprint_sqm: target.footprint_sqm,
      overrides: costLibrary?.extension_rates?.[target.type] || {},
      currency,
      project,
    });
    updateExtension(id, { shell_items: shell });
  };

  return (
    <div className="space-y-6 fc-fade-in" data-testid="extensions-tab">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h2 className="fc-heading text-xl text-[#1C1C1C]" data-testid="extensions-heading">
            Extensions &amp; new build
          </h2>
          <p className="text-xs text-[#A8A29E] mt-1 max-w-2xl" data-testid="extensions-description">
            Add new floor area or structural changes — single-storey rear extensions,
            double-storey additions, loft conversions, garage conversions, or
            internal knock-throughs. Shell costs are estimated per sqm of footprint;
            fitout uses the same room library as the rest of the project.
          </p>
        </div>
        <div className="flex items-center gap-3">
          {addedSqm > 0 && (
            <div className="text-right" data-testid="extensions-gla-badge">
              <div className="fc-label">New floor area</div>
              <div className="fc-heading text-lg text-[#1C1C1C] mt-0.5">
                +{addedSqm} sqm
              </div>
            </div>
          )}
          <div className="text-right">
            <div className="fc-label">Extensions total</div>
            <div className="fc-heading text-lg text-[#1C1C1C] mt-0.5" data-testid="extensions-grand-total">
              {fmt(grandTotal)}
            </div>
          </div>
          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <Button
                data-testid="add-extension-btn"
                className="bg-[#1C1C1C] text-white hover:bg-[#2A2A2E] rounded-lg h-9 px-3"
              >
                <Plus className="w-4 h-4 mr-1.5" /> Add extension
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white border-black/[0.08] text-[#27272A] sm:max-w-[680px] max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="fc-heading text-[#1C1C1C]">
                  New extension
                </DialogTitle>
                <DialogDescription className="text-xs text-[#A8A29E]">
                  Pick a type and (optionally) lay out the rooms inside.
                  Every room you add now is created with the right work-item
                  template — bathrooms get tiling and sanitary, kitchens get cabinetry, and so on.
                </DialogDescription>
              </DialogHeader>

              <div className="grid grid-cols-2 gap-3 py-2">
                <Field label="Name" colSpan={2}>
                  <Input
                    data-testid="new-extension-name-input"
                    value={draft.name}
                    onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                    placeholder="Rear single-storey extension"
                    className="fc-input h-10"
                  />
                </Field>
                <Field label="Type" colSpan={2}>
                  <SelectWrap
                    tid="new-extension-type-select"
                    value={draft.type}
                    onChange={(v) => setDraft({ ...draft, type: v })}
                    options={EXTENSION_TYPES}
                  />
                  <p className="text-[11px] text-[#78716C] mt-1.5" data-testid="new-extension-rate-hint">
                    Indicative shell rate at mid finish:{" "}
                    <span className="fc-mono text-[#1C1C1C]">
                      {draft.type === "knockthrough"
                        ? "Fixed cost (no new sqm)"
                        : `≈ ${fmt(indicativeShellRate(draft.type, currency))} / sqm of footprint`}
                    </span>
                  </p>
                </Field>
                {draft.type !== "knockthrough" && (
                  <Field label="Footprint (sqm)">
                    <Input
                      data-testid="new-extension-footprint-input"
                      type="number"
                      value={draft.footprint_sqm}
                      onChange={(e) => setDraft({ ...draft, footprint_sqm: e.target.value })}
                      className="fc-input h-10"
                    />
                  </Field>
                )}
              </div>

              {draft.type !== "knockthrough" && (
                <div className="border-t border-black/[0.06] pt-4 mt-1" data-testid="new-extension-rooms-section">
                  <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                    <div>
                      <div className="fc-label">Rooms inside this extension</div>
                      <p className="text-[11px] text-[#78716C] mt-0.5">
                        Lay out what's going inside. Total room sqm can't exceed the footprint.
                      </p>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <RoomBudgetBadge
                        used={draftUsedSqm}
                        total={draftFootprint}
                        over={draftOverbudget}
                      />
                      <Button
                        type="button"
                        data-testid="new-extension-add-room"
                        onClick={addDraftRoom}
                        variant="ghost"
                        disabled={draftFootprint <= 0}
                        className="h-8 text-xs border border-black/[0.08] hover:border-black/[0.2] disabled:opacity-50"
                      >
                        <Plus className="w-3.5 h-3.5 mr-1" /> Add room
                      </Button>
                    </div>
                  </div>

                  {(draft.rooms || []).length === 0 ? (
                    <div
                      className="text-xs text-[#A8A29E] text-center py-5 border border-dashed border-black/[0.06] rounded-md"
                      data-testid="new-extension-rooms-empty"
                    >
                      {draftFootprint > 0
                        ? "Optional — add a Lounge, Bedroom, Bathroom etc., or just hit \"Add extension\" and add rooms later."
                        : "Set a footprint above first."}
                    </div>
                  ) : (
                    <>
                      <BudgetBar used={draftUsedSqm} total={draftFootprint} over={draftOverbudget} />
                      <div className="space-y-2">
                        {(draft.rooms || []).map((r, idx) => (
                          <DraftRoomRow
                            key={r.tempId}
                            row={r}
                            index={idx}
                            onChange={(patch) => updateDraftRoom(r.tempId, patch)}
                            onRemove={() => removeDraftRoom(r.tempId)}
                          />
                        ))}
                      </div>
                    </>
                  )}

                  {draftOverbudget && (
                    <p
                      className="text-[11px] text-[#DC2626] mt-2"
                      data-testid="new-extension-overbudget-warning"
                    >
                      Rooms total {draftUsedSqm} sqm — {Math.abs(draftRemaining)} sqm over the {draftFootprint} sqm footprint. Reduce a room or increase the footprint.
                    </p>
                  )}
                </div>
              )}

              <div className="flex items-center justify-end gap-2 pt-4 border-t border-black/[0.06] mt-4">
                <Button
                  variant="ghost"
                  className="h-9"
                  onClick={() => setCreateOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  data-testid="confirm-add-extension-btn"
                  onClick={createExtension}
                  disabled={draftOverbudget}
                  className="bg-[#1C1C1C] text-white hover:bg-[#2A2A2E] rounded-lg h-9 px-3.5 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Plus className="w-4 h-4 mr-1.5" /> Add extension
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {extensions.length === 0 ? (
        <div
          className="fc-card p-12 text-center"
          data-testid="extensions-empty"
        >
          <Building2 className="w-10 h-10 text-sage-700 mx-auto" />
          <div className="fc-heading text-[#1C1C1C] mt-4">No extensions yet</div>
          <p className="text-xs text-[#57534E] mt-1 max-w-sm mx-auto">
            Adding a new bedroom, second storey or loft conversion?
            Click "Add extension" to model the shell and any new rooms.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {extensions.map((ext) => (
            <ExtensionCard
              key={ext.id}
              ext={ext}
              currency={currency}
              costLibrary={costLibrary}
              fmt={fmt}
              onChange={(patch) => updateExtension(ext.id, patch)}
              onDelete={() => deleteExtension(ext.id)}
              onRegenShell={() => regenShell(ext.id)}
              onAddExtension={() => setCreateOpen(true)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function ExtensionCard({ ext, currency, costLibrary, fmt, onChange, onDelete, onRegenShell, onAddExtension }) {
  const [open, setOpen] = useState(true);
  const [roomDialogOpen, setRoomDialogOpen] = useState(false);
  const [roomDraft, setRoomDraft] = useState({
    name: "",
    room_type: "bedroom",
    size_sqm: 12,
    finish_level: "mid",
  });
  const total = extensionTotal(ext);
  const shellTotal = (ext.shell_items || []).reduce(
    (s, it) => s + (Number(it.quantity) || 0) * (Number(it.rate) || 0), 0
  );
  const fitoutTotal = (ext.fitout_rooms || []).reduce(
    (s, r) => s + roomTotals(r).total, 0
  );
  const meta = EXTENSION_TYPES.find((t) => t.value === ext.type) || EXTENSION_TYPES[0];

  const updateShellItem = (id, patch) => {
    onChange({
      shell_items: (ext.shell_items || []).map((it) =>
        it.id === id ? { ...it, ...patch } : it
      ),
    });
  };
  const deleteShellItem = (id) => {
    onChange({
      shell_items: (ext.shell_items || []).filter((it) => it.id !== id),
    });
  };

  // Fitout rooms: reuse Room shape and RoomCard.
  const createFitoutRoom = () => {
    const meta = ROOM_TYPES.find((r) => r.value === roomDraft.room_type) || ROOM_TYPES[0];
    const size = Math.max(0, Number(roomDraft.size_sqm) || 0);
    const roomItems = buildDefaultWorkItems({
      room_type: roomDraft.room_type,
      size_sqm: size,
      renovation_scope: "moderate",
      finish_level: roomDraft.finish_level,
      condition: roomDraft.condition || "tired",
      overrides: costLibrary?.room_rates?.[roomDraft.room_type] || {},
      currency,
    });
    // New-build fitout rooms also need partitions, doors, ceilings, electrics
    // and glazing — items the renovation template doesn't carry because it
    // assumes an existing room.
    const extras = buildFitoutRoomExtras({
      room_type: roomDraft.room_type,
      size_sqm: size,
      overrides: costLibrary?.room_rates?.[roomDraft.room_type] || {},
      currency,
    });
    const room = {
      id: newId("room"),
      name: roomDraft.name?.trim() || meta.label,
      room_type: roomDraft.room_type,
      size_sqm: size,
      condition: "tired",
      renovation_scope: "moderate",
      finish_level: roomDraft.finish_level,
      work_items: [...extras, ...roomItems],
    };
    onChange({ fitout_rooms: [...(ext.fitout_rooms || []), room] });
    setRoomDialogOpen(false);
    setRoomDraft({
      name: "",
      room_type: "bedroom",
      size_sqm: Math.max(8, Math.round((Number(ext.footprint_sqm) || 0) / 2)),
      finish_level: "mid",
    });
  };
  const updateFitoutRoom = (id, patch) => {
    onChange({
      fitout_rooms: (ext.fitout_rooms || []).map((r) =>
        r.id === id ? { ...r, ...patch } : r
      ),
    });
  };
  const deleteFitoutRoom = (id) => {
    onChange({
      fitout_rooms: (ext.fitout_rooms || []).filter((r) => r.id !== id),
    });
  };

  return (
    <div
      className="fc-card p-0 overflow-hidden"
      data-testid={`extension-card-${ext.id}`}
    >
      <div className="p-5 border-b border-sage-200 bg-sage-50">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <button
              onClick={() => setOpen((v) => !v)}
              className="text-[#A8A29E] hover:text-[#1C1C1C]"
              data-testid={`toggle-extension-${ext.id}`}
              aria-label="Toggle extension"
            >
              {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            </button>
            <input
              data-testid={`extension-name-${ext.id}`}
              value={ext.name}
              onChange={(e) => onChange({ name: e.target.value })}
              className="fc-heading bg-transparent border-0 outline-none text-[#1C1C1C] text-[15px] font-medium w-full min-w-0 focus:outline-none"
            />
          </div>
          <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap justify-end">
            <span className="text-[10px] uppercase tracking-[0.15em] bg-white border border-sage-200 text-sage-700 rounded-full px-2 py-0.5">
              {meta.label}
            </span>
            <button
              data-testid={`regen-shell-${ext.id}`}
              onClick={onRegenShell}
              className="text-xs text-[#78716C] hover:text-[#1C1C1C] flex items-center gap-1 border border-black/[0.08] hover:border-black/[0.2] rounded-md px-2 py-1 transition-colors"
              title="Regenerate shell items at current footprint"
            >
              <RefreshCcw className="w-3 h-3" /> Regen shell
            </button>
            <button
              data-testid={`delete-extension-${ext.id}`}
              onClick={onDelete}
              className="text-[#A8A29E] hover:text-[#DC2626] transition-colors"
              aria-label="Delete extension"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
          {ext.type !== "knockthrough" && (
            <Field label="Footprint (sqm)">
              <Input
                data-testid={`extension-footprint-${ext.id}`}
                type="number"
                value={ext.footprint_sqm}
                onChange={(e) => onChange({ footprint_sqm: Number(e.target.value) || 0 })}
                className="fc-input h-9"
              />
            </Field>
          )}
          <Stat label="Shell" value={fmt(shellTotal)} />
          <Stat label="Fitout" value={fmt(fitoutTotal)} />
          <Stat label="Total" value={fmt(total)} valueClass="text-[#1C1C1C] font-semibold" />
        </div>
      </div>

      {open && (
        <div className="p-4 sm:p-5 space-y-6">
          {/* Shell items */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="fc-label">Shell costs</div>
                <p className="text-[11px] text-[#78716C] mt-0.5">
                  Foundation, structure, exterior, glazing, services tie-in. Editable.
                </p>
              </div>
            </div>
            {(ext.shell_items || []).length === 0 ? (
              <div className="text-xs text-[#A8A29E] py-6 text-center border border-dashed border-black/[0.06] rounded-md">
                No shell items. Click "Regen shell" to seed defaults.
              </div>
            ) : (
              <div className="overflow-x-auto fc-no-scrollbar -mx-4 sm:mx-0 px-4 sm:px-0">
                <table className="w-full min-w-[720px] text-sm">
                  <thead>
                    <tr className="text-left text-[10px] uppercase tracking-[0.15em] text-[#A8A29E]">
                      <th className="py-2 pr-2 font-normal min-w-[180px]">Name</th>
                      <th className="py-2 pr-2 font-normal w-24">Kind</th>
                      <th className="py-2 pr-2 font-normal w-20">Qty</th>
                      <th className="py-2 pr-2 font-normal w-20">Unit</th>
                      <th className="py-2 pr-2 font-normal w-28">Rate</th>
                      <th className="py-2 pr-2 font-normal w-28 text-right">Total</th>
                      <th className="py-2 pl-2 font-normal w-8"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {ext.shell_items.map((it) => (
                      <tr key={it.id} className="border-t border-black/[0.04]">
                        <td className="py-1.5 pr-2">
                          <input
                            data-testid={`shell-name-${it.id}`}
                            value={it.name}
                            onChange={(e) => updateShellItem(it.id, { name: e.target.value })}
                            className="fc-input h-8 px-2 text-sm w-full"
                          />
                        </td>
                        <td className="py-1.5 pr-2 text-xs uppercase tracking-[0.15em] text-[#78716C]">
                          {it.kind}
                        </td>
                        <td className="py-1.5 pr-2">
                          <Input
                            data-testid={`shell-qty-${it.id}`}
                            type="number"
                            value={it.quantity}
                            onChange={(e) => updateShellItem(it.id, { quantity: Number(e.target.value) || 0 })}
                            className="fc-input h-8 px-2 text-sm"
                          />
                        </td>
                        <td className="py-1.5 pr-2 text-xs text-[#78716C]">
                          {it.unit}
                        </td>
                        <td className="py-1.5 pr-2">
                          <Input
                            data-testid={`shell-rate-${it.id}`}
                            type="number"
                            value={it.rate}
                            onChange={(e) => updateShellItem(it.id, { rate: Number(e.target.value) || 0 })}
                            className="fc-input h-8 px-2 text-sm"
                          />
                        </td>
                        <td className="py-1.5 pr-2 text-right fc-mono text-[#1C1C1C]">
                          {fmt((Number(it.quantity) || 0) * (Number(it.rate) || 0))}
                        </td>
                        <td className="py-1.5 pl-2 text-right">
                          <button
                            data-testid={`delete-shell-${it.id}`}
                            onClick={() => deleteShellItem(it.id)}
                            className="text-[#A8A29E] hover:text-[#DC2626]"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Fitout rooms — only for footprint extensions, not knock-throughs */}
          {ext.type !== "knockthrough" && (
            <div>
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="fc-label">Fitout rooms</div>
                  <p className="text-[11px] text-[#78716C] mt-0.5">
                    Rooms inside this extension. Each one uses the same library and finish controls as the main Rooms tab.
                  </p>
                </div>
                <Button
                  data-testid={`add-fitout-room-${ext.id}`}
                  onClick={() => {
                    setRoomDraft({
                      name: "",
                      room_type: "bedroom",
                      size_sqm: Math.max(8, Math.round((Number(ext.footprint_sqm) || 0) / 2)),
                      finish_level: "mid",
                    });
                    setRoomDialogOpen(true);
                  }}
                  variant="ghost"
                  className="h-8 text-xs border border-black/[0.08] hover:border-black/[0.2]"
                >
                  <Plus className="w-3.5 h-3.5 mr-1" /> Add room
                </Button>
              </div>
              {(ext.fitout_rooms || []).length === 0 ? (
                <div className="text-xs text-[#A8A29E] py-6 text-center border border-dashed border-black/[0.06] rounded-md">
                  No fitout rooms yet. Add one to model what's inside this extension.
                </div>
              ) : (
                <div className="space-y-3">
                  {ext.fitout_rooms.map((room) => (
                    <RoomCard
                      key={room.id}
                      room={room}
                      onChange={(patch) => updateFitoutRoom(room.id, patch)}
                      onDelete={() => deleteFitoutRoom(room.id)}
                      onRegenerate={() => {
                        const items = buildDefaultWorkItems({
                          room_type: room.room_type,
                          size_sqm: room.size_sqm,
                          renovation_scope: room.renovation_scope,
                          finish_level: room.finish_level,
                          condition: room.condition || "tired",
                          overrides: costLibrary?.room_rates?.[room.room_type] || {},
                          currency,
                        });
                        const extras = buildFitoutRoomExtras({
                          room_type: room.room_type,
                          size_sqm: room.size_sqm,
                          overrides: costLibrary?.room_rates?.[room.room_type] || {},
                          currency,
                        });
                        updateFitoutRoom(room.id, { work_items: [...extras, ...items] });
                      }}
                      costLibrary={costLibrary}
                      currency={currency}
                    />
                  ))}
                </div>
              )}
            </div>
          )}

          {onAddExtension && (
            <div className="pt-4 mt-4 border-t border-black/[0.06] flex justify-center">
              <button
                type="button"
                data-testid={`footer-add-extension-${ext.id}`}
                onClick={onAddExtension}
                className="text-sm text-white bg-[#1C1C1C] hover:bg-[#2A2A2E] flex items-center gap-1.5 rounded-md px-4 py-2 transition-colors"
              >
                <Plus className="w-4 h-4" /> Add New Extension
              </button>
            </div>
          )}
        </div>
      )}

      <Dialog open={roomDialogOpen} onOpenChange={setRoomDialogOpen}>
        <DialogContent
          className="bg-white border-black/[0.08] text-[#27272A] sm:max-w-[560px] max-h-[90vh] overflow-y-auto"
          data-testid={`new-fitout-dialog-${ext.id}`}
        >
          <DialogHeader>
            <DialogTitle className="fc-heading text-[#1C1C1C]">
              New room in this extension
            </DialogTitle>
            <DialogDescription className="text-xs text-[#A8A29E]">
              Pick the room type so we seed the right work items — bathrooms get tiling and sanitary ware, kitchens get cabinetry, and so on.
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-2 gap-3 py-2">
            <Field label="Type" colSpan={2}>
              <SelectWrap
                tid={`new-fitout-type-${ext.id}`}
                value={roomDraft.room_type}
                onChange={(v) => setRoomDraft({ ...roomDraft, room_type: v })}
                options={ROOM_TYPES}
              />
            </Field>
            <Field label="Name" colSpan={2}>
              <Input
                data-testid={`new-fitout-name-${ext.id}`}
                value={roomDraft.name}
                onChange={(e) => setRoomDraft({ ...roomDraft, name: e.target.value })}
                placeholder={(ROOM_TYPES.find((r) => r.value === roomDraft.room_type) || {}).label || "Room name"}
                className="fc-input h-10"
              />
            </Field>
            <Field label="Size (sqm)">
              <Input
                data-testid={`new-fitout-size-${ext.id}`}
                type="number"
                value={roomDraft.size_sqm}
                onChange={(e) => setRoomDraft({ ...roomDraft, size_sqm: e.target.value })}
                className="fc-input h-10"
              />
            </Field>
            <Field label="Finish level">
              <SelectWrap
                tid={`new-fitout-finish-${ext.id}`}
                value={roomDraft.finish_level}
                onChange={(v) => setRoomDraft({ ...roomDraft, finish_level: v })}
                options={[
                  { value: "budget",  label: "Budget" },
                  { value: "mid",     label: "Mid" },
                  { value: "premium", label: "Premium" },
                ]}
              />
            </Field>
          </div>

          <div className="flex items-center justify-end gap-2 pt-2">
            <Button variant="ghost" className="h-9" onClick={() => setRoomDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              data-testid={`confirm-fitout-${ext.id}`}
              onClick={createFitoutRoom}
              className="bg-[#1C1C1C] text-white hover:bg-[#2A2A2E] rounded-lg h-9 px-3.5"
            >
              <Plus className="w-4 h-4 mr-1.5" /> Add room
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, children, colSpan = 1 }) {
  return (
    <div className={colSpan === 2 ? "col-span-2" : "min-w-0"}>
      <div className="fc-label mb-1.5">{label}</div>
      {children}
    </div>
  );
}

function RoomBudgetBadge({ used, total, over }) {
  if (!total) return null;
  const remaining = total - used;
  const cls = over
    ? "bg-red-50 text-[#DC2626] border-red-200"
    : remaining === 0
    ? "bg-sage-50 text-sage-700 border-sage-200"
    : "bg-white text-[#57534E] border-black/[0.08]";
  return (
    <span
      data-testid="new-extension-budget-badge"
      className={`text-[11px] fc-mono px-2 py-1 rounded-full border ${cls}`}
    >
      {used} / {total} sqm
    </span>
  );
}

function BudgetBar({ used, total, over }) {
  if (!total) return null;
  const pct = Math.min(100, Math.round((used / total) * 100));
  // Single-stop colour: green within budget, amber as it nears full, red when over.
  const colour = over
    ? "bg-[#DC2626]"
    : pct >= 90
    ? "bg-[#D97706]"
    : "bg-sage-700";
  return (
    <div
      className="h-1 rounded-full bg-black/[0.06] overflow-hidden mb-3"
      data-testid="new-extension-budget-bar"
      role="progressbar"
      aria-valuenow={used}
      aria-valuemax={total}
    >
      <div
        className={`${colour} h-full transition-all duration-300 ease-out`}
        style={{ width: `${over ? 100 : pct}%` }}
      />
    </div>
  );
}

function DraftRoomRow({ row, index, onChange, onRemove }) {
  return (
    <div
      data-testid={`new-extension-room-row-${index}`}
      className="grid grid-cols-12 gap-2 items-end bg-[#FAFAF9] border border-black/[0.04] rounded-md p-2.5"
    >
      <div className="col-span-4">
        <div className="fc-label mb-1">Type</div>
        <SelectWrap
          tid={`new-extension-room-type-${index}`}
          value={row.room_type}
          onChange={(v) => onChange({ room_type: v })}
          options={ROOM_TYPES}
        />
      </div>
      <div className="col-span-4">
        <div className="fc-label mb-1">Name</div>
        <Input
          data-testid={`new-extension-room-name-${index}`}
          value={row.name}
          onChange={(e) => onChange({ name: e.target.value })}
          placeholder={(ROOM_TYPES.find((r) => r.value === row.room_type) || {}).label || ""}
          className="fc-input h-9 text-sm"
        />
      </div>
      <div className="col-span-2">
        <div className="fc-label mb-1">Size (sqm)</div>
        <Input
          data-testid={`new-extension-room-size-${index}`}
          type="number"
          value={row.size_sqm}
          onChange={(e) => onChange({ size_sqm: e.target.value })}
          className="fc-input h-9 text-sm"
        />
      </div>
      <div className="col-span-1">
        <div className="fc-label mb-1">Finish</div>
        <SelectWrap
          tid={`new-extension-room-finish-${index}`}
          value={row.finish_level}
          onChange={(v) => onChange({ finish_level: v })}
          options={[
            { value: "budget",  label: "Bdgt" },
            { value: "mid",     label: "Mid" },
            { value: "premium", label: "Prem" },
          ]}
        />
      </div>
      <div className="col-span-1 flex justify-end">
        <button
          type="button"
          onClick={onRemove}
          data-testid={`new-extension-room-remove-${index}`}
          className="h-9 w-9 grid place-items-center text-[#A8A29E] hover:text-[#DC2626] hover:bg-red-50 rounded-md transition-colors"
          aria-label="Remove room"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

function Stat({ label, value, valueClass = "text-[#1C1C1C]" }) {
  return (
    <div>
      <div className="fc-label">{label}</div>
      <div className={`fc-mono text-sm mt-0.5 ${valueClass}`}>{value}</div>
    </div>
  );
}

function SelectWrap({ tid, value, onChange, options }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={tid} className="fc-input h-10 rounded-md">
        <SelectValue />
      </SelectTrigger>
      <SelectContent className="bg-white border-black/[0.08] text-[#27272A]">
        {options.map((o) => (
          <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

// Suppress unused export warnings — ROOM_TYPES referenced for completeness.
export { ROOM_TYPES };
